import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
  Description,
  Field,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

import { Button, useBreakpoint } from "~/shared";
import { customIcons as icons } from "~/ui";
import { ErrorText } from "~/ui/common/ErrorText";
import { tw } from "~/utils";

export interface Option<T> {
  label: string;
  value: T | null;
}

interface DropdownProps<T extends string | number = string> {
  label?: string;
  ariaLabel?: string;
  value?: T;
  onChange: (value: T | null) => void;
  options: Option<T>[];
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  error?: string;
  description?: ReactNode;
  onClose?: () => void;
}

const OPTIONS_LIMIT = 14;
const SCROLL_AMOUNT = 200;
const TIMEOUT_LAG = 100;

export function Dropdown<T extends string | number>({
  label,
  ariaLabel,
  value,
  onChange,
  options,
  disabled = false,
  placeholder = "Search...",
  className,
  error,
  description,
  onClose,
}: DropdownProps<T>) {
  const { isMobile } = useBreakpoint();
  const [query, setQuery] = useState("");
  const [hasMoreContent, setHasMoreContent] = useState(false);
  const [enableKeyboard, setEnableKeyboard] = useState(!isMobile);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const optionsRef = useRef<HTMLDivElement | null>(null);
  const checkTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setHasMoreContent(options.length > OPTIONS_LIMIT);
  }, [options.length]);

  const filteredOptions =
    query === ""
      ? options
      : options.filter((o) =>
          o.label.toLowerCase().includes(query.toLowerCase()),
        );

  const selected = options.find((option) => option.value === value);

  const checkHasMoreContent = () => {
    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
    }

    checkTimeoutRef.current = setTimeout(() => {
      if (optionsRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = optionsRef.current;
        const hasMore = scrollTop + clientHeight < scrollHeight - 1;
        setHasMoreContent(hasMore);
      }
    }, TIMEOUT_LAG);
  };

  const handleChange = (val: T | null) => {
    onChange(val);
    if (Boolean(val) && isMobile) {
      setTimeout(() => {
        inputRef.current?.blur();
        setEnableKeyboard(false);
      }, TIMEOUT_LAG);
    }
  };

  const handleFocus = () => {
    if (isMobile && !enableKeyboard) {
      inputRef.current?.blur();
    }
    setTimeout(() => {
      containerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setEnableKeyboard(true);
    }, TIMEOUT_LAG);
  };

  const handleTouchEnd = () => {
    if (enableKeyboard) {
      inputRef.current?.focus();
      setEnableKeyboard(false);
      return;
    }
    inputRef.current?.blur();
  };

  const handleBlur = () => {
    setEnableKeyboard(false);
  };

  const handleClose = () => {
    handleBlur();
    onClose?.();
  };

  const handleScroll = () => {
    checkHasMoreContent();
  };

  const handleScrollDown = () => {
    if (optionsRef.current) {
      if (inputRef.current) {
        inputRef.current.blur();
      }

      optionsRef.current.scrollBy({
        top: SCROLL_AMOUNT,
        behavior: "smooth",
      });
      checkHasMoreContent();
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    checkHasMoreContent();
  };

  return (
    <Field className={tw("flex flex-col gap-0", className)} ref={containerRef}>
      <Combobox
        value={value ?? null}
        onChange={handleChange}
        disabled={disabled}
        immediate
        onClose={handleClose}
      >
        {label && (
          <ComboboxLabel className="text-indigo-charcoal">
            {label}
          </ComboboxLabel>
        )}
        <div className="min-w-50 relative">
          <div
            className={tw(
              "relative w-full cursor-default overflow-hidden rounded-md border bg-white text-left",
              selected?.value
                ? "border-violet bg-light-lavender-gray/10"
                : "border-dropdown-border placeholder:text-dropdown-placeholder",
              disabled &&
                "border-dropdown-disabled-border text-dropdown-disabled-text",
              error && "bg-error border-red-400",
            )}
          >
            <ComboboxInput
              ref={inputRef}
              aria-label={ariaLabel}
              aria-invalid={!!error}
              className="text-midnight-indigo placeholder:text-footer-text disabled:text-dropdown-disabled-text w-full border-none py-3 pl-4 pr-10 text-base leading-5 focus:outline-none focus:ring-0"
              displayValue={() => selected?.label ?? ""}
              onChange={handleSearch}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onTouchEnd={handleTouchEnd}
              placeholder={placeholder}
            />
            <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDownIcon
                className="h-8 w-8 text-gray-400"
                aria-hidden="true"
              />
            </ComboboxButton>
          </div>
          <ComboboxOptions
            ref={optionsRef}
            modal={false}
            onScroll={handleScroll}
            className={tw(
              "max-h-140 absolute z-10 mb-1 mt-1 w-full overflow-auto rounded-md bg-white text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:max-h-80",
            )}
          >
            {filteredOptions.length === 0 ? (
              <div className="cursor-default select-none px-4 py-2 text-gray-500">
                No results
              </div>
            ) : (
              filteredOptions.map((option) => (
                <ComboboxOption
                  key={option.value}
                  value={option.value}
                  disabled={option.value === null}
                  className={({ focus: active }) =>
                    tw(
                      "relative cursor-pointer select-none whitespace-nowrap py-2.5 pl-10 pr-6",
                      active ? "bg-indigo-50 text-indigo-900" : "text-gray-700",
                    )
                  }
                >
                  {({ selected }) => (
                    <>
                      <span className="block">{option.label}</span>
                      {selected && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                          <icons.CheckedCheckbox />
                        </span>
                      )}
                    </>
                  )}
                </ComboboxOption>
              ))
            )}
            <div
              className={tw(
                "transition-height sticky bottom-0 z-20 flex justify-center duration-300",
                hasMoreContent
                  ? "opacity-100"
                  : "pointer-events-none opacity-0",
              )}
            >
              <Button
                onClick={handleScrollDown}
                onTouchStart={handleScrollDown}
                variant="ghost"
                size="icon"
                className="h-5 w-5 text-gray-600 hover:bg-gray-50 hover:shadow-lg"
                aria-label="Scroll down to see more options"
              >
                <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
              </Button>
            </div>
          </ComboboxOptions>
        </div>
      </Combobox>
      {error && <Description as={ErrorText}>{error}</Description>}
      {description && <Description as="div">{description}</Description>}
    </Field>
  );
}
